import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const CALLOUTS = [{
  phase: 'Launch',
  title: 'Novelty — long format',
  desc: 'When a treatment is new, HCPs have both curiosity and a professional responsibility to stay informed. That creates room for longer-form education and longer intervals between exposures.',
  surface: 'border-[var(--accent)]/35 bg-[var(--accent)]/[0.07]',
  label: 'text-[var(--accent)]'
}, {
  phase: 'Foundational Brands',
  title: 'Short & frequent',
  desc: 'As a treatment becomes a Foundational Brand, that learning imperative fades. Communication therefore needs to become shorter, sharper and more frequent.',
  surface: 'border-[var(--brand)]/30 bg-[var(--brand)]/[0.06]',
  label: 'text-[var(--brand)]'
}] as const;
const MAIN_CURVE = 'M 80 290 C 140 290 170 380 230 380 C 290 380 340 340 380 290 C 410 250 430 130 460 90 C 540 70 650 70 730 90 C 745 95 760 245 785 275 C 850 283 920 287 960 290';
const FORMAT_LINE = 'M 380 78 C 470 96 545 118 615 148 C 700 186 800 224 950 248';
const FREQUENCY_LINE = 'M 380 248 C 500 238 585 214 675 172 C 765 130 855 100 950 78';
const PHASES = [{
  label: 'R&D',
  sub: 'Investment & clinical trials',
  x: 230
}, {
  label: 'Launch',
  sub: 'Heavy marketing spend',
  x: 420
}, {
  label: 'Mature',
  sub: 'Peak revenue & share',
  x: 595
}, {
  label: 'Foundational Brands',
  sub: 'Loss of exclusivity',
  x: 845,
  accent: true
}];
const AXIS = '#D8DEE7';
export function SlideFormatByPhase({}: SlideProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [setTimeout(() => setStage(1), 400), setTimeout(() => setStage(2), 1300), setTimeout(() => setStage(3), 2600), setTimeout(() => setStage(4), 3800)];
    return () => timers.forEach(clearTimeout);
  }, []);
  return <InteriorSlide surface data-id="element-359" data-mp="illvm1beotye">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} transition={{
      duration: 0.5
    }} className="text-center flex-shrink-0" data-id="element-360" data-mp="11ph3hykpatot">
        <p className="text-[var(--brand)] text-[clamp(0.7rem,1vw,0.9rem)] font-semibold uppercase tracking-[0.18em] mb-2" data-id="element-361" data-mp="xnvjlt9r1f54">
          Format & Frequency
        </p>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(1.75rem,3.2vw,2.9rem)] leading-tight tracking-tight" data-id="element-362" data-mp="1le7w7hfxw021">
          As familiarity grows, format shortens
        </h2>
        <p className="text-[var(--muted)] text-[clamp(0.85rem,1.2vw,1.05rem)] max-w-2xl mx-auto mt-3" data-id="element-363" data-mp="1beh1bengvj1f">
          New treatments create a reason to learn. Foundational Brands need a
          reason to re-engage.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 relative flex items-center justify-center py-2" data-id="element-364" data-mp="2g91pre9cgx">
        <div className="w-full max-w-5xl max-h-full relative" style={{
        aspectRatio: '2.2 / 1'
      }} data-id="element-365" data-mp="4x3q53k2j4lh">
          <svg viewBox="0 0 1000 480" className="w-full h-full overflow-visible" data-id="element-366" data-mp="3omrrpcqxqy95">
            {/* Axis annotations */}
            <text x="60" y="100" textAnchor="middle" className="text-xs font-medium" fill="#9CA3AF" data-id="element-367" data-mp="6f2ffthzq8r1">
              +
            </text>
            <text x="60" y="385" textAnchor="middle" className="text-xs font-medium" fill="#9CA3AF" data-id="element-368" data-mp="39xc3o4ish6x5">
              −
            </text>
            <text x="30" y="240" textAnchor="middle" className="text-xs font-medium" fill="#6B7280" style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed'
          }} data-id="element-369" data-mp="3joqcx40jll3h">
              Cumulative Value
            </text>
            <text x="520" y="455" textAnchor="middle" className="text-xs font-medium" fill="#6B7280" data-id="element-370" data-mp="2zvjmtjel2pvx">
              Time
            </text>
            <text x="975" y="295" className="text-[10px] font-medium" fill="#9CA3AF" data-id="element-371" data-mp="39qbg4b14uqkw">
              €0
            </text>

            {/* Axes */}
            <line x1="80" y1="40" x2="80" y2="430" stroke={AXIS} strokeWidth="2" data-id="element-372" data-mp="3ilhq7m7frut8" />
            <line x1="80" y1="290" x2="970" y2="290" stroke={AXIS} strokeWidth="2" data-id="element-373" data-mp="3njjaubkig26t" />

            {/* Phase dividers */}
            {[380, 460, 730].map(x => <line key={x} x1={x} y1="50" x2={x} y2="430" stroke={AXIS} strokeWidth="1" strokeDasharray="4,4" data-id="element-374" data-mp="1btdwpxvi597" />)}

            {/* Product journey curve */}
            <path d={MAIN_CURVE} fill="none" stroke="#1A1A1A" strokeWidth="3" data-id="element-375" data-mp="kao7hhg8iou0" />

            {/* Format length — long to short */}
            <motion.path d={FORMAT_LINE} fill="none" stroke="#6A2C91" strokeWidth="3" strokeLinecap="round" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 2 ? 1 : 0
          }} transition={{
            duration: 0.9,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-376" data-mp="bg027rant757" />
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 2 ? 1 : 0
          }} transition={{
            duration: 0.25,
            delay: 0.6
          }} data-id="element-377" data-mp="u8u8zxy4psyc">
              <text x="380" y="62" textAnchor="middle" className="text-xs font-semibold" fill="#6A2C91" data-id="element-378" data-mp="3a0i2tkhuj5cx">
                Long
              </text>
              <text x="958" y="252" className="text-xs font-semibold" fill="#6A2C91" data-id="element-379" data-mp="32ffnyfagqshg">
                Short
              </text>
              <text x="958" y="266" className="text-[10px]" fill="#4E1F6D" data-id="element-380" data-mp="37c62uzaj7i1a">
                Content length
              </text>
            </motion.g>

            {/* Frequency & share of voice — rising */}
            <motion.path d={FREQUENCY_LINE} fill="none" stroke="#F36633" strokeWidth="3" strokeLinecap="round" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.9,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-381" data-mp="2xgul083ttupb" />
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.25,
            delay: 0.7
          }} data-id="element-382" data-mp="1v41km6nrtvvm">
              <text x="958" y="75" className="text-xs font-semibold" fill="#F36633" data-id="element-383" data-mp="201jwa4t6ap7b">
                Frequency
              </text>
              <text x="958" y="89" className="text-xs font-semibold" fill="#F36633" data-id="element-384" data-mp="2e01cziknv1fq">
                &amp; SOV
              </text>
            </motion.g>

            {/* Phase labels */}
            {PHASES.map(p => <g key={p.label} data-id="element-385" data-mp="2it4a20onct0o">
                <text x={p.x} y="450" textAnchor="middle" className="text-sm font-semibold" fill={p.accent ? '#F36633' : '#374151'} data-id="element-386" data-mp="2itvwom09nget">
                  {p.label}
                </text>
                <text x={p.x} y="468" textAnchor="middle" className="text-xs" fill={p.accent ? '#C2410C' : '#9CA3AF'} data-id="element-387" data-mp="31dhcpef5q6e">
                  {p.sub}
                </text>
              </g>)}
            <text x="230" y="410" textAnchor="middle" className="text-[10px] italic" fill="#9CA3AF" data-id="element-388" data-mp="gzbwvy4h7snh">
              Investment
            </text>
            <text x="595" y="55" textAnchor="middle" className="text-[10px] italic" fill="#9CA3AF" data-id="element-389" data-mp="3leti868tjlet">
              Peak Revenue
            </text>
          </svg>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: stage >= 4 ? 1 : 0,
      y: stage >= 4 ? 0 : 12
    }} transition={{
      duration: 0.4,
      ease: [0.23, 1, 0.32, 1]
    }} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto w-full flex-shrink-0 mt-4" data-id="element-390" data-mp="2xnxuplbebcdd">
        {CALLOUTS.map(c => <div key={c.phase} className={`rounded-2xl border p-4 ${c.surface}`} data-id="element-391" data-mp="2l4k23ualk4ue">
            <div className="flex items-baseline gap-2 mb-1.5" data-id="element-392" data-mp="2g6ikldstkdl1">
              <span className={`text-[10px] font-bold uppercase tracking-wider ${c.label}`} data-id="element-393" data-mp="2b7xl5gle5xxn">
                {c.phase}
              </span>
              <h3 className="text-[var(--fg)] font-bold text-sm" data-id="element-394" data-mp="26fnvm1o6uzvw">{c.title}</h3>
            </div>
            <p className="text-[var(--muted)] text-[13px] leading-snug" data-id="element-395" data-mp="21gb77cho5kr7">
              {c.desc}
            </p>
          </div>)}
      </motion.div>
    </InteriorSlide>;
}